import type {BaseFilterKeys} from '@igp/shared/api';
import type {DateRangeFilterValue, MultiKeyFilterTextValue} from '@igp/shared/components/filter';
import type {
    AgentPlayerReportingV2,
    AgentPlayerReportingV2Page,
    BonusCostSummary,
    CasinoSummary,
    GlobalSpinsSummary,
    PlayerActivitySummary,
    PlayerEcologySummary,
    PokerMttSummary,
    PokerNlheSummary,
    PokerPloSummary,
    PokerSummary,
    ShareSummary,
    SportsSummary,
    TotalGgrSummary,
} from '@igp/shared/contract/pam';
import type {AgentFilterValue} from '@igp/shared/features/agent-profile';
import type {BaseSettingsType} from '@igp/shared/user-settings';
import type {Path} from '@igp/shared/utils';

export const ServiceTypes = {
    DownloadClientStrategy: Symbol('PlayerLevelReportV2DownloadClientStrategy'),
    DownloadServerStrategy: Symbol('PlayerLevelReportV2DownloadServerStrategy'),
    PlayerLevelReportV2Service: Symbol('PlayerLevelReportV2Service'),
} as const;

export const summaryId = 'AgentPlayerReportingV2SummaryId';

export type PlayerLevelReportV2QueryKeys = Path<AgentPlayerReportingV2Page>;

export type PlayerLevelReportV2DownloadFields = Path<AgentPlayerReportingV2>;

export type PlayerLevelReportV2QueryTextFilterKeys = 'uid_un' | 'uid' | 'username';

export type PlayerLevelReportV2QueryFilterKeys =
    | BaseFilterKeys
    | PlayerLevelReportV2QueryTextFilterKeys
    | 'country'
    | 'referrer_id'
    | 'is_downstream'
    | 'started_at_from'
    | 'started_at_to'
    | 'started_at_min';

export type PlayerLevelReportV2FilterViewModelTextKeys = 'uid_username' | 'uid' | 'username';

export type PlayerLevelReportV2FilterViewModel = {
    text: MultiKeyFilterTextValue<PlayerLevelReportV2FilterViewModelTextKeys>;
    agent: AgentFilterValue;
    countries: string[];
    startedAt: DateRangeFilterValue;
};

export type PlayerLevelReportV2FilterName = keyof Omit<PlayerLevelReportV2FilterViewModel, 'text'> | 'uid_username';

export class BonusCostSummaryViewModel {
    cash_realized_bonus?: number;
    cost_ftd_bonus_pro?: number;
    cost_psp_fee_pro?: number;
    cost_tournament_overlay?: number;
    cost_tournament_overlay_excl_freeroll?: number;
    mtt_ticket_bonus?: number;
    poker_bonus?: number;
    psp_fee?: number;
}

export class CasinoSummaryViewModel {
    casino_bonus?: number;
    casino_ggr?: number;
    casino_ngr?: number;
    casino_share?: number;
    external_casino_ggr?: number;
    internal_casino_ggr?: number;
    casino_wager?: number;
}

export class GlobalSpinsSummaryViewModel {
    cost_global_spins_tickets?: number;
    global_spins_expected_ggr?: number;
}

export class PlayerActivitySummaryViewModel {
    cash_game_hands_played?: number;
    ecology_flag?: string;
    mtt_played?: number;
    player_profit_loss?: number;
    player_win_loss?: number;
    net_deposit?: number;
}

export type IsPro = 'Mixed' | 'Pro' | 'NonPro';

export type PlayerEcologySummaryViewModel = {
    ecology_flag: string;
    is_pro_shortdeck: IsPro;
    is_pro_zoom: IsPro;
    stake_2_is_pro_nlhe: IsPro;
    stake_4_is_pro_nlhe: IsPro;
    stake_10_is_pro_nlhe: IsPro;
    stake_20_is_pro_nlhe: IsPro;
    stake_50_is_pro_nlhe: IsPro;
    stake_100_is_pro_nlhe: IsPro;
    stake_200_is_pro_nlhe: IsPro;
    stake_400_is_pro_nlhe: IsPro;
};

export class PokerMttSummaryViewModel {
    mtt_ggr?: number;
    mtt_ngr?: number;
    mtt_ticket_bonus?: number;
    mtt_total_buyin?: number;
    player_mtt_win_loss?: number;
    poker_mtt_fees?: number;
}

export class PokerNlheSummaryViewModel {
    is_pro?: number;
    is_pro_shortdeck?: number;
    nlhe_add_on_ggr_usd_pro?: number;
    nlhe_ggr_usd_pro?: number;
    nlhe_insurance_ggr_usd_pro?: number;
    nlhe_jackpot_ggr_usd_pro?: number;
    nlhe_rakes_ggr_usd_pro?: number;
    nlhe_micro_ggr_usd_pro?: number;
    nlhe_low_ggr_usd_pro?: number;
    nlhe_medium_ggr_usd_pro?: number;
    nlhe_high_ggr_usd_pro?: number;
    player_nlhe_shortdeck_win_loss_lifetime?: number;
    ggr_poker_prev_180_days?: number;
    player_nlhe_shortdeck_win_loss_prev_180_days?: number;
    shortdeck_add_on_ggr_usd_pro?: number;
    shortdeck_ggr_usd_pro?: number;
    shortdeck_insurance_ggr_usd_pro?: number;
    shortdeck_rakes_ggr_usd_pro?: number;
}

export class PokerPloSummaryViewModel {
    plo_ggr?: number;
}

export class PokerSummaryViewModel {
    poker_addons?: number;
    poker_addons_add_on?: number;
    poker_addons_guess?: number;
    poker_addons_insurance?: number;
    poker_ggr?: number;
    poker_ggr_usd_pro_ecology_modified?: number;
    poker_ngr?: number;
    poker_rake?: number;
    poker_share?: number;
}

export class ShareSummaryViewModel {
    casino_share?: number;
    global_spins_expected_ggr?: number;
    mtt_ggr?: number;
    plo_ggr?: number;
    poker_share?: number;
    sports_settled_bet?: number;
}

export class SportsSummaryViewModel {
    sports_ggr?: number;
    sports_settled_bet?: number;
}

export class TotalGgrSummaryViewModel {
    ggr?: number;
}

export class PlayerLevelReportV2ViewModel {
    bonus_cost_summary?: BonusCostSummaryViewModel;
    casino_summary?: CasinoSummaryViewModel;
    global_spins_summary?: GlobalSpinsSummaryViewModel;
    iso_alpha2_country_code?: string;
    path?: string;
    player_activity_summary?: PlayerActivitySummaryViewModel;
    player_ecology?: PlayerEcologySummaryViewModel;
    poker_mtt_summary?: PokerMttSummaryViewModel;
    poker_nlhe_summary?: PokerNlheSummaryViewModel;
    poker_plo_summary?: PokerPloSummaryViewModel;
    poker_summary?: PokerSummaryViewModel;
    referrer_agent_id?: string;
    referrer_agent_username?: string;
    share_summary?: ShareSummaryViewModel;
    sports_summary?: SportsSummaryViewModel;
    uid: string;
    username?: string;
    total_ggr_summary?: TotalGgrSummaryViewModel;
    isSummary: boolean;
}

export type PlayerLevelReportV2ViewModelKeys =
    | Path<Omit<PlayerLevelReportV2ViewModel, 'isSummary'>>
    | 'global_spins_summary.global_spins_ggr';

export class PlayerLevelReportV2DownloadModel implements Record<PlayerLevelReportV2ViewModelKeys, string> {
    iso_alpha2_country_code: string;
    path: string;
    referrer_agent_id: string;
    referrer_agent_username: string;
    uid: string;
    username: string;
    'total_ggr_summary.ggr': string;
    'bonus_cost_summary.cash_realized_bonus': string;
    'bonus_cost_summary.cost_ftd_bonus_pro': string;
    'bonus_cost_summary.cost_psp_fee_pro': string;
    'bonus_cost_summary.cost_tournament_overlay': string;
    'bonus_cost_summary.cost_tournament_overlay_excl_freeroll': string;
    'bonus_cost_summary.mtt_ticket_bonus': string;
    'bonus_cost_summary.poker_bonus': string;
    'bonus_cost_summary.psp_fee': string;
    'casino_summary.casino_bonus': string;
    'casino_summary.casino_ggr': string;
    'casino_summary.casino_ngr': string;
    'casino_summary.casino_share': string;
    'casino_summary.external_casino_ggr': string;
    'casino_summary.internal_casino_ggr': string;
    'casino_summary.casino_wager': string;
    'global_spins_summary.cost_global_spins_tickets': string;
    'global_spins_summary.global_spins_expected_ggr': string;
    'player_activity_summary.cash_game_hands_played': string;
    'player_activity_summary.ecology_flag': string;
    'player_activity_summary.mtt_played': string;
    'player_activity_summary.player_profit_loss': string;
    'player_activity_summary.player_win_loss': string;
    'player_activity_summary.net_deposit': string;
    'player_ecology.ecology_flag': string;
    'player_ecology.is_pro_shortdeck': string;
    'player_ecology.is_pro_zoom': string;
    'player_ecology.stake_2_is_pro_nlhe': string;
    'player_ecology.stake_4_is_pro_nlhe': string;
    'player_ecology.stake_10_is_pro_nlhe': string;
    'player_ecology.stake_20_is_pro_nlhe': string;
    'player_ecology.stake_50_is_pro_nlhe': string;
    'player_ecology.stake_100_is_pro_nlhe': string;
    'player_ecology.stake_200_is_pro_nlhe': string;
    'player_ecology.stake_400_is_pro_nlhe': string;
    'poker_mtt_summary.mtt_ticket_bonus': string;
    'poker_mtt_summary.mtt_ggr': string;
    'poker_mtt_summary.mtt_ngr': string;
    'poker_mtt_summary.mtt_total_buyin': string;
    'poker_mtt_summary.player_mtt_win_loss': string;
    'poker_mtt_summary.poker_mtt_fees': string;
    'poker_nlhe_summary.is_pro': string;
    'poker_nlhe_summary.is_pro_shortdeck': string;
    'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_micro_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_low_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_medium_ggr_usd_pro': string;
    'poker_nlhe_summary.nlhe_high_ggr_usd_pro': string;
    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime': string;
    'poker_nlhe_summary.ggr_poker_prev_180_days': string;
    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days': string;
    'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro': string;
    'poker_nlhe_summary.shortdeck_ggr_usd_pro': string;
    'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro': string;
    'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro': string;
    'poker_plo_summary.plo_ggr': string;
    'poker_summary.poker_addons': string;
    'poker_summary.poker_addons_add_on': string;
    'poker_summary.poker_addons_guess': string;
    'poker_summary.poker_addons_insurance': string;
    'poker_summary.poker_ggr': string;
    'poker_summary.poker_ggr_usd_pro_ecology_modified': string;
    'poker_summary.poker_ngr': string;
    'poker_summary.poker_rake': string;
    'poker_summary.poker_share': string;
    'share_summary.casino_share': string;
    'share_summary.global_spins_expected_ggr': string;
    'share_summary.mtt_ggr': string;
    'share_summary.plo_ggr': string;
    'share_summary.poker_share': string;
    'share_summary.sports_settled_bet': string;
    'sports_summary.sports_settled_bet': string;
    'sports_summary.sports_ggr': string;
    'global_spins_summary.global_spins_ggr': string;
}

export interface PlayerLevelReportV2ColumnSetting extends BaseSettingsType, Record<PlayerLevelReportV2ViewModelKeys, null> {}

export type PlayerLevelReportV2ColumnSettingKeys = keyof PlayerLevelReportV2ColumnSetting;

export type PlayerLevelReportV2SortKeys =
    | Path<
          Pick<
              AgentPlayerReportingV2,
              'uid' | 'username' | 'referrer_agent_id' | 'referrer_agent_username' | 'iso_alpha2_country_code' | 'path'
          >
      >
    | Path<Omit<TotalGgrSummary, '__typename'>>
    | Path<Omit<BonusCostSummary, '__typename'>>
    | Path<Omit<CasinoSummary, '__typename'>>
    | Path<Omit<GlobalSpinsSummary, '__typename'>>
    | Path<Omit<PlayerActivitySummary, '__typename'>>
    | Path<Omit<PlayerEcologySummary, '__typename'>>
    | Path<Omit<PokerMttSummary, '__typename'>>
    | Path<Omit<PokerNlheSummary, '__typename'>>
    | Path<Omit<PokerPloSummary, '__typename'>>
    | Path<Omit<PokerSummary, '__typename'>>
    | Path<Omit<ShareSummary, '__typename' | 'total_share'>>
    | Path<Omit<SportsSummary, '__typename'>>
    | 'global_spins_ggr';
